<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { ProjectUserAssociationCreateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { SHARING_USER_ROLES, SharingModeSelectOptions } from '@/constants';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  projectId: number;
  projectName: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

const show = computed<boolean>(() => {
  return props.show;
});

// --- Component state ---
const userEmail = ref('');
const userRole = ref(SHARING_USER_ROLES.VIEWER);
const description = ref('');

// --- Mutation: Create Project-User Association ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectUserAssociationCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectUserAssociationCreate) {
    console.debug(
      'Project-user association created:',
      res.data.projectUserAssociationCreate
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Project-user association failed: ' + res.data;
    console.error('Project-user association failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error creating project-user association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onProjectUserAssociationRequested() {
  if (!userEmail.value) {
    mutationError.value = 'User email not provided';
    console.error('User email not provided');
    return;
  }
  console.debug(
    'Creating project-user association, project ',
    props.projectId,
    'user',
    userEmail.value
  );

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    projectId: props.projectId,
    userEmail: userEmail.value,
    userRole: userRole.value,
    description: description.value,
  });
}

function closeModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Create Project-User Association -->
    <b-modal v-model="show" @hide="closeModal">
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="plus" class="me-2" />
        Share project
      </template>
      <!-- Modal Content -->
      <div>
        <BFormGroup label="User e-mail" label-for="user-email" class="mt-3">
          <BFormInput
            v-model="userEmail"
            id="user-email"
            name="user-email"
            placeholder="Enter user e-mail"
            required
          ></BFormInput>
        </BFormGroup>

        <BFormGroup label="User role" label-for="user-role" class="mt-3">
          <b-form-select
            id="user-role"
            v-model="userRole"
            :options="SharingModeSelectOptions"
            class="form-control mb-2"
          />
        </BFormGroup>

        <BFormGroup label="Description" label-for="description" class="mt-3">
          <b-form-textarea
            id="description"
            v-model="description"
            class="form-control mb-2"
          >
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
          </b-form-textarea>
        </BFormGroup>

        <!-- Error Mutation -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="closeModal"> Close </b-button>
        <b-button
          variant="primary"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onProjectUserAssociationRequested"
        >
          <span v-if="mutationInProgress">
            Sharing Project
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Sharing Successful
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Share Project </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
